/*  iceberg-ui  */
import { Icon, IconWithVariant } from '@auviknetworks/iceberg-ui';

/*  MUI  */
import { Box, IconButton, TextField } from '@mui/material';

/* Shared */
import { DarkTooltip } from 'application/Shared/Components/Tooltips';
import { FontSizes } from 'application/Shared/Constants';
import { Notify } from 'application/Shared/Helpers';
import { debug } from 'application/Shared/Utils';
import { ClientLink } from 'application/Shared/types/clientLink';

/* hooks */
import { usePutTenant } from 'hooks/dataAccess/Tenant/tenant';

/*  React  */
import React, { Dispatch, SetStateAction, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

interface EditNameProps {
    currentName: string | undefined;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    putTenant: (params: any) => Promise<boolean>;
    reloadSessionPartnerTenantLinks: () => void;
    row: ClientLink;
    setModifyingName: Dispatch<SetStateAction<string | boolean>>;
    setCurrentName: Dispatch<SetStateAction<string | undefined>>;
}

const EditName = ({
    currentName,
    putTenant,
    reloadSessionPartnerTenantLinks,
    row,
    setModifyingName,
    setCurrentName
}: EditNameProps) => {
    const { t } = useTranslation();
    const [modifiedName, setModifiedName] = useState(currentName);

    const saveName = async () => {
        const { linkedTenantId } = row;
        debug.dev('Saving Name', modifiedName, row);

        if (!modifiedName?.trim() || modifiedName.trim() === currentName) {
            setModifyingName(false);
            return;
        }

        try {
            const response = await putTenant({
                id: linkedTenantId,
                params: {
                    name: modifiedName.trim(),
                    partnerUpdate: true
                }
            });

            setModifyingName(false);
            if (response) {
                reloadSessionPartnerTenantLinks();
                setCurrentName(modifiedName.trim());
                Notify.s(t('Partner.dashboard.client_name_saved'));
            } else {
                Notify.e(t('Partner.dashboard.client_name_error'));
            }
        } catch (e) {
            const err = t('Partner.dashboard.client_name_error');
            debug.e(err, e);
            Notify.e(err);
        }
    };

    return (
        <Box display="flex" data-testid="editName">
            <TextField
                value={modifiedName}
                fullWidth
                size="small"
                variant="outlined"
                inputProps={{
                    sx: { padding: '4px', width: '400px' }
                }}
                onChange={(e) => setModifiedName(e.target.value)}
            />
            <Box display="flex" flexDirection="column">
                <IconButton
                    data-testid="clientNameEditSaveBtn"
                    variant="slim"
                    onClick={() => saveName()}
                    sx={{ color: 'icon.success' }}
                >
                    <Icon size={FontSizes[2]} src="CheckCircle" />
                </IconButton>
                <IconButton
                    data-testid="clientNameEditCancelBtn"
                    variant="slim"
                    sx={{ color: 'icon.error' }}
                    onClick={() => setModifyingName(false)}
                >
                    <Icon size={FontSizes[2]} src="CloseCircle" />
                </IconButton>
            </Box>
        </Box>
    );
};

interface ClientNameProps {
    internalId: string | null;
    reloadSessionPartnerTenantLinks: () => void;
    routeToClient: string;
    row: ClientLink;
}

export const ClientName = ({
    internalId,
    reloadSessionPartnerTenantLinks,
    routeToClient,
    row
}: ClientNameProps) => {
    const { t } = useTranslation();
    const [modifyingName, setModifyingName] = useState<boolean | string>(false);
    const { id, tenant } = row;
    const { id: clientId, name } = tenant || {};
    const [currentName, setCurrentName] = useState(name);

    const { putTenant } = usePutTenant();

    if (modifyingName) {
        return (
            <EditName
                currentName={currentName}
                putTenant={putTenant}
                reloadSessionPartnerTenantLinks={
                    reloadSessionPartnerTenantLinks
                }
                row={row}
                setModifyingName={setModifyingName}
                setCurrentName={setCurrentName}
            />
        );
    }

    return (
        <Box display="flex" alignItems="flex-start" flex={1}>
            <Box display="flex">
                {clientId === internalId && (
                    <DarkTooltip arrow title={t('Common.internalTenant')}>
                        <Box>
                            <IconWithVariant
                                data-testid="clientIsInternalIcon"
                                variant="primary"
                                src="HomeFilled"
                                size={FontSizes[0]}
                                wrapperProps={{ marginRight: '2px' }}
                            />
                        </Box>
                    </DarkTooltip>
                )}
                <Box sx={{ textWrap: 'balance' }}>
                    <Link
                        className="link underline"
                        to={routeToClient}
                        data-testid="clientNameLink"
                    >
                        {currentName}
                    </Link>
                    {modifyingName !== id && (
                        <IconButton
                            data-testid="clientNameStartEditBtn"
                            variant="slim"
                            color="secondary"
                            onClick={() => {
                                setModifyingName(true);
                            }}
                        >
                            <Icon size={FontSizes[1]} src="Pencil" />
                        </IconButton>
                    )}
                </Box>
            </Box>
        </Box>
    );
};
